import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/store/auth'
import { useGuildStore } from '@/store/guilds'
import { describeError } from '@/lib/auth'
import { applyForumEvent } from '@/lib/forums'
import { channelLevel, channelMuted, messageNotificationLevel, NOTIFY_ALL, NOTIFY_INHERIT, NOTIFY_MENTIONS, NOTIFY_NOTHING, ownMuted } from '@/lib/muting'
import type { Channel } from '@/types/api'
import { Modal } from '../settings/Modal'
import styles from './ForumChannel.module.css'

export function ForumNotifications({ thread, onClose }: { thread: Channel; onClose: () => void }) {
  const guildId = thread.guild_id ?? ''
  const channels = useGuildStore(s => s.channels[guildId])
  const settings = useGuildStore(s => s.settings[guildId])
  const guild = useGuildStore(s => s.guilds[guildId])
  const current = channels?.[thread.id] ?? thread
  const client = useQueryClient()
  const [level, setLevel] = useState(() => channelLevel(settings, thread.id))
  const [muted, setMuted] = useState(() => ownMuted(settings, thread.id))
  const [busy, setBusy] = useState(false)
  const pending = useRef(false)
  const [error, setError] = useState<string | null>(null)
  const inheritedSettings = settings && { ...settings, channel_overrides: settings.channel_overrides.filter(override => override.channel_id !== thread.id) }
  const inherited = messageNotificationLevel(inheritedSettings, current, channels, guild?.default_message_notifications)
  const inheritedName = inherited === NOTIFY_ALL ? 'все ответы' : inherited === NOTIFY_NOTHING ? 'ничего' : 'упоминания'
  const inheritedMute = Boolean(current.parent_id && channelMuted(settings, current.parent_id, channels))
  const subscribe = async () => {
    const userId = useAuthStore.getState().user?.id
    if (!userId || pending.current) return
    pending.current = true; setBusy(true); setError(null)
    try {
      const store = useGuildStore.getState()
      if (current.member) await store.leaveThread(guildId, thread.id)
      else await store.joinThread(guildId, thread.id)
      if (useAuthStore.getState().user?.id !== userId) return
      const next = useGuildStore.getState().channels[guildId]?.[thread.id]
      if (next) applyForumEvent(client, userId, 'THREAD_MEMBERS_UPDATE', { id: next.id, parent_id: next.parent_id, guild_id: guildId, added_members: next.member ? [next.member] : [], removed_member_ids: next.member ? [] : [userId] })
      void client.invalidateQueries({ queryKey: ['inbox', userId] })
    } catch (err) { setError(describeError(err).message) }
    finally { pending.current = false; setBusy(false) }
  }
  const save = async () => {
    const userId = useAuthStore.getState().user?.id
    if (!userId || pending.current) return
    pending.current = true; setBusy(true); setError(null)
    try {
      await useGuildStore.getState().updateSettings(guildId, { channel_overrides: { [thread.id]: { message_notifications: level, muted } } })
      if (useAuthStore.getState().user?.id === userId) onClose()
    } catch (err) { setError(describeError(err).message) }
    finally { pending.current = false; setBusy(false) }
  }
  return <Modal title="Уведомления темы" width={460} onClose={() => { if (!pending.current) onClose() }}>
    <form className={styles.form} onSubmit={event => { event.preventDefault(); void save() }}>
      <p className={styles.inForum}>{current.name}</p>
      <label className={styles.notificationChoice}><input type="checkbox" checked={Boolean(current.member)} disabled={busy || !current.member && current.thread_metadata?.archived} onChange={() => void subscribe()} />Следить за темой</label>
      <p className={styles.inForum}>Ответ в теме включает подписку</p>
      <fieldset className={styles.notificationLevels} disabled={busy}><legend>О чём уведомлять</legend>
        {[{ value: NOTIFY_INHERIT, label: `По умолчанию · ${inheritedName}` }, { value: NOTIFY_ALL, label: 'Все ответы' }, { value: NOTIFY_MENTIONS, label: 'Только @упоминания' }, { value: NOTIFY_NOTHING, label: 'Ничего' }].map(option => <label key={option.value} className={styles.notificationChoice}><input type="radio" name={`forum-notifications-${thread.id}`} value={option.value} checked={level === option.value} onChange={() => setLevel(option.value)} />{option.label}</label>)}
      </fieldset>
      <label className={styles.notificationChoice}><input type="checkbox" checked={muted} disabled={busy} onChange={event => setMuted(event.target.checked)} />Заглушить тему</label>
      {inheritedMute ? <p className={styles.inForum}>Форум или сервер заглушён</p> : null}
      {error ? <p className={styles.error} role="alert">{error}</p> : null}
      <div className={styles.formActions}><button type="button" className={styles.cancel} disabled={busy} onClick={onClose}>Закрыть</button><button type="submit" className={styles.create} disabled={busy}>{busy ? 'Сохраняем…' : 'Сохранить'}</button></div>
    </form>
  </Modal>
}
